<template>
    <div class="flex lg:flex-row md:flex-row flex-col gap-8 p-4">
        <!-- Left content -->
        <div class="flex-1 text-2xl">
            <h1 class="text-4xl text-lime-600">
                {{ $t("patients.preventionAndLongevity.title") }}
            </h1>
            <div
                class="mt-4"
                v-html="
                    `<p>${$t('patients.preventionAndLongevity.prevention.content1')}</p>`
                "
            ></div>
            <div class="relative inline-block">
                <!-- Dugme -->
                <button
                    ref="buttonRef"
                    @click="toggleTooltip"
                    class="px-4 py-2 bg-lime-600 text-white rounded-lg"
                >
                    {{
                        $t(
                            "patients.preventionAndLongevity.prevention.readMore",
                        )
                    }}
                </button>

                <!-- Tooltip -->
                <div
                    v-if="showTooltip"
                    ref="tooltipRef"
                    class="absolute z-50 mt-2 left-1/2 -translate-x-1/2"
                    :class="tooltipPositionClass"
                >
                    <div class="relative">
                        <!-- Tooltip telo -->
                        <div
                            class="max-w-48 w-max break-words whitespace-normal bg-gray-800 text-white text-sm px-3 py-2 rounded-lg shadow-lg"
                        >
                            {{
                                $t(
                                    "patients.preventionAndLongevity.prevention.tooltipContent",
                                )
                            }}
                        </div>

                        <!-- Strelica -->
                        <div
                            class="absolute left-1/2 -translate-x-1/2 w-3 h-3 rotate-45 bg-gray-800"
                            :class="arrowPositionClass"
                        ></div>
                    </div>
                </div>
            </div>
            <div
                class="mt-4"
                v-html="
                    `<p>${$t('patients.preventionAndLongevity.prevention.content')}</p>`
                "
            ></div>
        </div>
        <!-- Right content -->
        <div class="md:w-1/2 flex flex-col items-start text-2xl">
            <div class="bg-lime-600 text-white p-6 rounded-xl shadow-lg w-full">
                <h3 class="text-xl font-bold mb-4">
                    {{ $t("patients.preventionAndLongevity.prevention.title") }}
                </h3>
                <ul class="space-y-2 mb-2">
                    <li>
                        ①
                        {{
                            $t(
                                "patients.preventionAndLongevity.prevention.list.bookAnAppointment.title",
                            )
                        }}
                        <span class="float-right"></span>
                    </li>
                    <li>
                        ②
                        {{
                            $t(
                                "patients.preventionAndLongevity.prevention.list.firstConsultation.title",
                            )
                        }}
                        <span class="float-right">1799 NOK - 30 min</span>
                        <ul
                            class="mt-2 ml-4 list-disc text-xl space-y-1 text-white"
                        >
                            <li>
                                {{
                                    $t(
                                        "patients.preventionAndLongevity.prevention.list.firstConsultation.children.findOutWhichTestsAreRelevant",
                                    )
                                }}
                            </li>
                            <li>
                                {{
                                    $t(
                                        "patients.preventionAndLongevity.prevention.list.firstConsultation.children.explainToYouWhatInformationTheTestResultGivesYou",
                                    )
                                }}
                            </li>
                            <li>
                                {{
                                    $t(
                                        "patients.preventionAndLongevity.prevention.list.firstConsultation.children.orderTheTestFromAuthorizedScandinavianLaboratory",
                                    )
                                }}
                            </li>
                        </ul>
                    </li>
                    <li>
                        ③
                        {{
                            $t(
                                "patients.preventionAndLongevity.prevention.list.secondConsultationAfter4Weeks.title",
                            )
                        }}
                        <span class="float-right">1999 NOK - 30 min</span>
                        <ul
                            class="mt-2 ml-4 list-disc text-xl space-y-1 text-white"
                        >
                            <li>
                                {{
                                    $t(
                                        "patients.preventionAndLongevity.prevention.list.secondConsultationAfter4Weeks.children.theInterpretationOfTheResultsAndPersonalizedPlan",
                                    )
                                }}
                            </li>
                        </ul>
                    </li>
                    <li>
                        ④
                        {{
                            $t(
                                "patients.preventionAndLongevity.prevention.list.followUpAfter3To6Months.title",
                            )
                        }}
                        <span class="float-right">1999 NOK - 30 min</span>
                    </li>
                </ul>
            </div>

            <!-- Дугмад ИСПОД зелене картице -->
            <div class="mt-4 w-full flex flex-col gap-3">
                <NuxtLink :to="$localeRoute('/patients/virtual-clinic')">
                    <button
                        class="text-3xl text-white font-bold w-full py-3 rounded-lg bg-lime-700"
                    >
                        {{
                            $t(
                                "patients.preventionAndLongevity.prevention.list.enroll.title",
                            ).toUpperCase()
                        }}
                    </button>
                </NuxtLink>
                <NuxtLink :to="$localeRoute('/patients/packages')">
                    <button
                        class="text-3xl bg-orange-500 text-white font-bold w-full py-3 rounded-lg"
                    >
                        {{
                            $t(
                                "patients.preventionAndLongevity.prevention.list.chooseThePackageThatFitsYouBest.title",
                            ).toUpperCase()
                        }}
                    </button>
                </NuxtLink>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import PreventionCard from "~/components/PreventionCard.vue";
import GroupCard from "~/components/GroupCard.vue";
import { useLanguagesStore } from "~/stores/languages";
const languageStore = useLanguagesStore();

import { ref, onMounted, onBeforeUnmount, computed, nextTick } from "vue";

const showTooltip = ref(false);
const tooltipRef = ref(null);
const buttonRef = ref(null);
const tooltipPosition = ref("bottom"); // 'bottom', 'top', 'left', 'right'

function toggleTooltip() {
    showTooltip.value = !showTooltip.value;

    if (showTooltip.value) {
        nextTick(() => {
            adjustTooltipPosition();
        });
    }
}

function handleClickOutside(event) {
    const clickedOutside =
        !tooltipRef.value?.contains(event.target) &&
        !buttonRef.value?.contains(event.target);
    if (clickedOutside) {
        showTooltip.value = false;
    }
}

function adjustTooltipPosition() {
    const tooltip = tooltipRef.value;
    const button = buttonRef.value;

    if (!tooltip || !button) return;

    const tooltipRect = tooltip.getBoundingClientRect();
    const buttonRect = button.getBoundingClientRect();
    const screenWidth = window.innerWidth;

    // If tooltip overflows left or right, adjust position
    if (tooltipRect.left < 0) {
        tooltipPosition.value = "left";
    } else if (tooltipRect.right > screenWidth) {
        tooltipPosition.value = "right";
    } else {
        tooltipPosition.value = "bottom";
    }
}

const tooltipPositionClass = computed(() => {
    switch (tooltipPosition.value) {
        case "top":
            return "bottom-full mb-2";
        case "left":
            return "right-full mr-2 top-1/2 -translate-y-1/2";
        case "right":
            return "left-full ml-2 top-1/2 -translate-y-1/2";
        default:
            return "";
    }
});

const arrowPositionClass = computed(() => {
    switch (tooltipPosition.value) {
        case "top":
            return "top-full";
        case "left":
            return "top-1/2 left-full -translate-y-1/2";
        case "right":
            return "top-1/2 right-full -translate-y-1/2";
        default:
            return "top-0"; // for bottom
    }
});

onMounted(() => {
    document.addEventListener("click", handleClickOutside);
});

onBeforeUnmount(() => {
    document.removeEventListener("click", handleClickOutside);
});
// Make href reactive
const href = computed(() =>
    languageStore.getSelectedLanguage === "no"
        ? "https://booking.pridok.no/appointment/e6af7ac5-8fcb-48d7-9f39-fb1ee283a522/KAR/df5ef6a7-f1f4-4003-9433-a92233d6c067"
        : "/contact",
);
</script>

<style scoped></style>
